import { useAppDispatch } from "../../../redux-store";
import { useSelector } from "react-redux";
import {
  selectInvoiceLines,
  selectUnits,
} from "../../../ducks/data/finance/selectors";
import React, { useContext, useEffect, useState } from "react";
import {
  loadFinanceDataForDriver,
  loadInvoiceLinesWithRevision,
  loadInvoiceLines,
  loadPricingAgreements,
} from "../../../ducks/data/finance";
import Button from "@mui/material/Button";
import { InvoiceLine } from "../../../ducks/data/finance/types";
import AddEditProductModal from "../../SessionsPage/modals/AddEditProductModal";
import useFeature from "../../../hooks/useFeature";
import { SocketContext } from "../../../SocketConnection";

const ProductsButtons = ({ cargoDeliveryId }: { cargoDeliveryId: string }) => {
  const dispatch = useAppDispatch();
  const invoiceLines = useSelector(selectInvoiceLines(cargoDeliveryId));
  const [selectedInvoiceLine, setSelectedInvoiceLine] =
    useState<InvoiceLine | null>(null);

  const loadInvoiceLinesWithRevisionEnabled = useFeature(
    "driver-app-caching__delivery-revision-number"
  );
  // When on, invoice-line updates are driven by socket nudges instead of the
  // interval poll (the two are mutually exclusive).
  const invoiceLinesViaWebsockets = useFeature(
    "mobile-app-invoice-lines-websockets"
  );
  const socket = useContext(SocketContext);

  useEffect(() => {
    const loadFinanceData = () => {
      dispatch(loadFinanceDataForDriver());
      dispatch(loadPricingAgreements({ deliveryId: cargoDeliveryId }));
      // Invoice lines are only polled here when NOT using websockets — when on,
      // the socket effect below drives invoice-line refetches. The rest of the
      // finance data (products/surcharges/units, pricing agreements) keeps
      // polling regardless, since the socket nudge only covers invoice lines.
      if (!invoiceLinesViaWebsockets) {
        if (loadInvoiceLinesWithRevisionEnabled) {
          dispatch(
            loadInvoiceLinesWithRevision({ deliveryId: cargoDeliveryId })
          );
        } else {
          dispatch(loadInvoiceLines({ deliveryId: cargoDeliveryId }));
        }
      }
    };

    loadFinanceData();
    const interval = setInterval(() => loadFinanceData(), 10000);
    return () => {
      clearInterval(interval);
    };
  }, [
    dispatch,
    cargoDeliveryId,
    loadInvoiceLinesWithRevisionEnabled,
    invoiceLinesViaWebsockets,
  ]);

  // Live refetch trigger (used instead of polling when the websockets feature is
  // on): join this delivery's room and refetch its invoice lines when the
  // server signals the revision changed. Re-joining + refetching on reconnect
  // reconciles any nudge missed while offline. The payload guard avoids
  // refetching for a different delivery when multiple are mounted.
  useEffect(() => {
    if (!socket || !invoiceLinesViaWebsockets) {
      return;
    }
    const refetch = (payload?: { deliveryId?: string }) => {
      if (payload?.deliveryId && payload.deliveryId !== cargoDeliveryId) {
        return;
      }
      dispatch(loadInvoiceLinesWithRevision({ deliveryId: cargoDeliveryId }));
    };
    const joinAndReconcile = () => {
      socket.emit("join-invoice-lines", { deliveryId: cargoDeliveryId });
      refetch();
    };
    if (socket.connected) {
      joinAndReconcile();
    }
    socket.on("connect", joinAndReconcile);
    socket.on("driver-invoice-lines.revision-changed", refetch);
    return () => {
      socket.emit("leave-invoice-lines", { deliveryId: cargoDeliveryId });
      socket.off("connect", joinAndReconcile);
      socket.off("driver-invoice-lines.revision-changed", refetch);
    };
  }, [socket, cargoDeliveryId, dispatch, invoiceLinesViaWebsockets]);

  return (
    <>
      {selectedInvoiceLine && (
        <AddEditProductModal
          cargoDeliveryId={cargoDeliveryId}
          invoiceLine={selectedInvoiceLine}
          onClose={() => setSelectedInvoiceLine(null)}
        />
      )}
      {invoiceLines.map((il) => (
        <InvoiceLineProductButton
          key={il.id}
          invoiceLine={il}
          onClick={() => setSelectedInvoiceLine(il)}
          disabled={il.locked}
        />
      ))}
    </>
  );
};

const InvoiceLineProductButton = (props: {
  invoiceLine: InvoiceLine;
  onClick: () => void;
  disabled: boolean;
}) => {
  const { invoiceLine, onClick, disabled } = props;
  const units = useSelector(selectUnits);
  const name = invoiceLine.description;
  const unit = invoiceLine.unitId
    ? units.find((u) => u.id === invoiceLine.unitId)?.name
    : "";
  return (
    <Button
      key={invoiceLine.id}
      onClick={onClick}
      disabled={disabled}
      style={{
        background: disabled ? "rgba(0, 0, 0, 0.12)" : "var(--success-500)",
        border: disabled
          ? "rgba(0, 0, 0, 0.12)"
          : "1px solid var(--success-500)",
        color: disabled ? "rgba(0, 0, 0, 0.26)" : "white",
      }}
      variant="outlined"
      size="large"
      fullWidth
    >
      {name} {invoiceLine.quantity} {invoiceLine.quantity ? unit : ""}
    </Button>
  );
};

export default ProductsButtons;
